import { RiMovieLine, RiImage2Line, RiGeminiFill } from "@remixicon/react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { PresetItem } from "./PresetContent";

const formatIconMap = {
  video: (
    <span className="flex items-center justify-center w-[20px] h-[20px] rounded-[4px] bg-[#018C62B3] backdrop-blur-[4px]">
      <RiMovieLine size={14} className="text-white" />
    </span>
  ),
  image: (
    <span className="flex items-center justify-center w-[20px] h-[20px] rounded-[4px] bg-[#FF4699B3] backdrop-blur-[4px]">
      <RiImage2Line size={14} className="text-white" />
    </span>
  ),
};

interface PresetCardProps {
  item: PresetItem;
  onClick?: (item: PresetItem) => void;
  className?: string;
}

const PresetCard = ({ item, onClick, className }: PresetCardProps) => {
  const { title, description, thumbnail, formats, isAI } = item;

  return (
    <div
      className={cn(
        "group flex flex-col w-[296px] h-[208px] pt-[8px] px-[8px] pb-[12px] rounded-[12px] bg-white cursor-pointer hover:shadow-md transition-shadow border border-[#E7E8E9] shadow-sm",
        className,
      )}
      style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}
      onClick={() => onClick?.(item)}
    >
      {/* Thumbnail - clips from bottom on hover */}
      <div className="relative w-full h-[120px] group-hover:h-[8px] rounded-[8px] group-hover:rounded-[6px] overflow-hidden transition-[height,border-radius] duration-300 ease-out">
        <div className="absolute inset-x-0 top-0 h-[120px] flex items-center justify-center bg-[#EDEFF3]">
          {thumbnail ? (
            <img src={thumbnail} alt={title} className="w-full h-full object-cover" />
          ) : (
            <RiImage2Line size={36} className="text-[#B5BCCB]" />
          )}

          {/* Format badges */}
          {formats && formats.length > 0 && (
            <div className="absolute bottom-[8px] left-[8px] flex items-center gap-[4px]">
              {formats.map((format) => (
                <span key={format}>{formatIconMap[format]}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Title */}
      <div className="flex items-center gap-[6px] mt-[12px] px-[6px]">
        <span className="text-[13px] font-medium leading-[18px] text-[#35467A] truncate">
          {title}
        </span>
        {isAI && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="shrink-0 inline-flex cursor-default">
                  <svg width="0" height="0" style={{ position: "absolute" }}>
                    <defs>
                      <linearGradient
                        id="ai-icon-gradient"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                      >
                        <stop offset="0%" stopColor="#FF4500" />
                        <stop offset="100%" stopColor="#FF4699" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <RiGeminiFill
                    size={14}
                    style={{ fill: "url(#ai-icon-gradient)", opacity: 0.7 }}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-[11px]">
                AI Section
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Description — wrapper clips text and grows smoothly via flex as the thumbnail collapses */}
      <div className="flex-1 min-h-0 overflow-hidden mt-[4px] px-[6px]">
        <p className="text-[12px] font-normal leading-[16px] text-[#67749B] line-clamp-2 group-hover:line-clamp-none">
          {description}
        </p>
      </div>
    </div>
  );
};

export default PresetCard;
